// AUTO-PAUSE — reading the reason the health monitor left behind.
//
// `campaignHealthMonitor.js` auto-pauses a campaign on systemic AI malfunction
// (duplicate openers, dead air, slow openers past threshold). It flips
// `status: 'paused'`, stamps `pausedAt`, and records WHY by prefixing a marker
// onto `campaigns.description`:
//
//   [⚠️ 2026-07-28T10:15:33Z AUTO-PAUSED by health monitor — dead-air 45% (9/20) in last 30min]
//
// built as: `[⚠️ ${new Date().toISOString().slice(0, 19)}Z ${reason}]`
//
// So the reason IS already persisted — it has simply never been rendered. Until
// core gains a dedicated column (routed to Charlie), this parses the marker so a
// user can see that the platform paused their campaign and what tripped it.
//
// ⚠️ Two consequences of the reason living in `description`, both handled by
// callers rather than here:
//   1. The marker must never be shown inside a description EDITOR — it is not the
//      user's text. `stripPauseMarker` gives the human part.
//   2. A page that loaded BEFORE the pause holds a description with no marker.
//      Echoing that back on save ERASES the reason. CampaignDetail therefore only
//      sends `description` when the user actually changed it.

// Tolerates the warning sign with or without its variation selector (U+FE0F).
// Anchored at the start — the monitor always prefixes.
//
// The literal "AUTO-PAUSED by health monitor" is REQUIRED, not optional. Campaign
// descriptions are a free-text field the user can type into (Campaigns.jsx), and a
// looser pattern matched ordinary human text: "[⚠ do not dial before 10am] Houston
// list" parsed as an auto-pause whose reason was "not dial before 10am". Combined
// with a manual pause that produced a fault banner built entirely out of the
// user's own note.
const MARKER = /^\[⚠️?\s*(\S+)\s+(AUTO-PAUSED by health monitor[\s\S]*?)\]\s*/;

// How far apart `pausedAt` and the marker's own timestamp may be and still be
// considered the same event. They are written in ONE `prisma.campaign.update`, so
// in practice this is milliseconds; the tolerance only absorbs clock skew.
const SAME_EVENT_MS = 120_000;

// → { at: Date|null, reason: string } when the campaign was auto-paused, else null.
export function parsePauseMarker(description) {
  const m = MARKER.exec(String(description || ''));
  if (!m) return null;
  const [, stamp, reason] = m;
  const at = new Date(stamp);
  return {
    at: Number.isNaN(at.getTime()) ? null : at,
    reason: (reason || '').trim()
  };
}

// The description with the marker removed — what a human wrote, and the only
// thing that belongs in an editor.
export function stripPauseMarker(description) {
  return String(description || '').replace(MARKER, '').trimStart();
}

// True when this campaign is paused AND the monitor is the one that paused it.
//
// ⚠️ The marker is NEVER CLEARED. `transitionStatus` writes only status/pausedAt/
// startedAt/completedAt, and no dashboard path rewrites `description` — so once a
// campaign has been auto-paused, the marker stays in its description forever.
// Presence of a marker therefore means "was auto-paused at SOME point", not "is
// auto-paused now".
//
// Without correlating the timestamps: monitor auto-pauses Tuesday → the agent is
// fixed and the campaign restarted → on Friday the user clicks Pause themselves →
// the banner announces "Alfred paused this campaign automatically… nobody on your
// team did this" and shows Tuesday's evidence. It would fire on the next manual
// pause of any campaign the monitor has ever tripped.
//
// The monitor writes `pausedAt` and the marker stamp in ONE update, so the pause
// is the monitor's only when the two line up.
export function isAutoPaused(campaign) {
  if (campaign?.status !== 'paused') return false;

  // AUTHORITATIVE since migration 0051: core sets `pausedBy` in the same write as
  // the pause and CLEARS both it and pauseReason when the campaign leaves 'paused'.
  // Mirrors campaignService.isAutoPaused exactly — this is the same flag the server
  // uses to refuse "Call now", so the screen and the gate cannot disagree.
  if (campaign.pausedBy === 'auto:health') return true;
  if (campaign.pausedBy) return false; // 'user' — a deliberate human pause.

  // FALLBACK for rows paused BEFORE 0051, which have no pausedBy. The marker in
  // `description` is all those rows carry, and it is never cleared — so it only
  // means "was auto-paused at some point" and has to be correlated against
  // pausedAt. New rows never reach here.
  const marker = parsePauseMarker(campaign?.description);
  if (!marker?.at) return false;
  // No pausedAt to correlate against — don't guess, and don't blame the platform.
  if (!campaign?.pausedAt) return false;
  const pausedAt = new Date(campaign.pausedAt);
  if (Number.isNaN(pausedAt.getTime())) return false;
  return Math.abs(pausedAt.getTime() - marker.at.getTime()) <= SAME_EVENT_MS;
}

// The reason to SHOW. Prefer core's `pauseReason` column (0051+); fall back to the
// description marker for older rows.
export function pauseReasonOf(campaign) {
  if (campaign?.pauseReason) return { reason: campaign.pauseReason, at: campaign.pausedAt ? new Date(campaign.pausedAt) : null };
  return parsePauseMarker(campaign?.description);
}

// Turn the monitor's terse reason into something a non-engineer can act on.
//
// Each sentence must describe ONLY what the corresponding detector actually
// measures. This is a fault report — inventing a consequence the monitor never
// observed sends someone to look for the wrong thing:
//   • hasDuplicateConsecutiveAI — ANY two identical consecutive AI turns, anywhere
//     in the call. Core's own comment notes it catches mid-call loops, so scoping
//     this to "openers" would misdirect an audit.
//   • isDeadAir — the caller spoke and the AI produced ZERO turns on a completed
//     call of 12s+. That is an agent-start / TTS failure (the silent-line class),
//     NOT the AI trailing off partway through.
//   • slow-opener — openerMs over the threshold. The monitor measures no hangups,
//     dispositions or durations, so it cannot support "people hung up".
const EXPLAIN = [
  [/duplicate-opener/i, 'The AI repeated the same line twice in a row.'],
  [/dead-air/i, 'The caller spoke and the AI never said anything at all.'],
  [/slow-opener/i, 'The AI took too long to speak first.']
];

export function explainPause(reason) {
  const hits = EXPLAIN.filter(([re]) => re.test(reason || '')).map(([, text]) => text);
  return hits.length ? hits.join(' ') : null;
}
